import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Custom Currency Component - Heaven's Bounty / Sofu Stacks
const BountyDisplay = ({ amount, size = 'md' }) => {
  const sizeClasses = {
    sm: 'text-sm',
    md: 'text-xl',
    lg: 'text-3xl',
    xl: 'text-5xl'
  };
  
  return (
    <div className={`flex items-center gap-2 ${sizeClasses[size]} font-bold`}>
      <img 
        src="/bounty-stack-token.png" 
        alt="Heaven's Bounty" 
        className={size === 'xl' ? 'w-12 h-12' : size === 'lg' ? 'w-8 h-8' : 'w-6 h-6'}
      />
      <span className="bg-gradient-to-r from-amber-600 to-yellow-500 bg-clip-text text-transparent">
        {amount.toLocaleString()} Stacks
      </span>
    </div>
  );
};

// Game Disclaimer Component
const GameDisclaimer = () => (
  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-center text-xs text-amber-800 mt-4">
    <p className="font-semibold">✝️ Faith Enrichment Games ✝️</p>
    <p className="mt-1">
      "Heaven's Bounty" and "Sofu Stacks" are spiritual reward tokens designed to affirm your growth 
      and knowledge in the Christian faith. These are not real currency and have no monetary value.
    </p>
  </div>
);

const TrickyTestamentGame = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const edition = searchParams.get('edition') || 'adult'; // youth or adult
  
  const [score, setScore] = useState(0);
  const [showResult, setShowResult] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [gameOver, setGameOver] = useState(false);
  const [questions, setQuestions] = useState([]);
  const [answeredQuestions, setAnsweredQuestions] = useState([]);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [showBoard, setShowBoard] = useState(true);
  const [dailyDoubleRevealed, setDailyDoubleRevealed] = useState(false);
  const [dailyDoubleIndex, setDailyDoubleIndex] = useState(null);
  const [wager, setWager] = useState(0);

  // Board configuration — adjusted by access level
  const [accessLevel, setAccessLevel] = useState('demo');
  const [unlockedSeries, setUnlockedSeries] = useState([]);

  const FULL_POINT_VALUES = [100, 200, 300, 400, 500];
  const DEMO_POINT_VALUES = [100, 200];
  const pointValues = accessLevel === 'full' ? FULL_POINT_VALUES : DEMO_POINT_VALUES;
  const boardSize = pointValues.length * 5; // 5 categories * rows

  const categories = edition === 'youth' 
    ? ['Women of Faith', 'Patriarchs', 'Courage & Trust', 'Covenant & Identity', 'Who Am I?']
    : ['Covenant (4Cs)', 'Cross & Redemption', 'Women of Wisdom', 'Faith in Motion', 'Deep Cuts'];

  useEffect(() => {
    fetchQuestions();
  }, [edition]);

  const fetchQuestions = async () => {
    try {
      const age = edition === 'youth' ? 'youth' : 'adult';
      const token = localStorage.getItem('soul_food_token');
      const headers = token ? { 'Authorization': `Bearer ${token}` } : {};

      const res = await fetch(
        `${BACKEND_URL}/api/trivia/questions/for-game?game_type=trivia_testament&age_group=${age}`,
        { headers }
      );
      if (res.ok) {
        const data = await res.json();
        const allQ = data.questions || [];
        const level = data.access_level || 'demo';
        setAccessLevel(level);
        setUnlockedSeries(data.unlocked_series || []);

        const rows = level === 'full' ? 5 : 2;
        const needed = rows * 5; // 5 categories

        if (allQ.length > 0) {
          // Group questions by character/category for proper columns
          const byCategory = {};
          allQ.forEach(q => {
            const cat = q.category_title || q.character || 'Bible Knowledge';
            if (!byCategory[cat]) byCategory[cat] = [];
            byCategory[cat].push(q);
          });

          // Pick top 5 categories with most questions, shuffle within each
          const sortedCats = Object.entries(byCategory)
            .sort((a, b) => b[1].length - a[1].length)
            .slice(0, 5);

          const formatted = [];
          let id = 0;
          const pvs = level === 'full' ? FULL_POINT_VALUES : DEMO_POINT_VALUES;

          sortedCats.forEach(([, catQs], catIndex) => {
            // Sort by difficulty for escalating point values
            const sorted = [...catQs].sort((a, b) => {
              const order = { easy: 0, medium: 1, hard: 2, expert: 3 };
              return (order[a.difficulty] || 1) - (order[b.difficulty] || 1);
            });
            // Shuffle within same difficulty for variety
            const shuffled = sorted.sort((a, b) => {
              if (a.difficulty === b.difficulty) return Math.random() - 0.5;
              const order = { easy: 0, medium: 1, hard: 2, expert: 3 };
              return (order[a.difficulty] || 1) - (order[b.difficulty] || 1);
            });

            for (let row = 0; row < rows; row++) {
              const q = shuffled[row % shuffled.length]; // wrap if not enough
              formatted.push({
                id: id++,
                categoryIndex: catIndex,
                pointIndex: row,
                category: categories[catIndex],
                question: q.question,
                correct_answer: q.correct_answer,
                explanation: q.explanation || '',
                scripture_ref: q.scripture || '',
                points: pvs[row],
              });
            }
          });

          // If we have fewer than 5 categories from DB, fill remaining with overflow questions
          if (sortedCats.length < 5) {
            const remaining = allQ.sort(() => Math.random() - 0.5);
            let qi = 0;
            for (let catIndex = sortedCats.length; catIndex < 5; catIndex++) {
              for (let row = 0; row < rows; row++) {
                const q = remaining[qi % remaining.length];
                qi++;
                formatted.push({
                  id: id++,
                  categoryIndex: catIndex,
                  pointIndex: row,
                  category: categories[catIndex],
                  question: q.question,
                  correct_answer: q.correct_answer,
                  explanation: q.explanation || '',
                  scripture_ref: q.scripture || '',
                  points: pvs[row],
                });
              }
            }
          }

          setQuestions(formatted);
          const ddIndex = Math.floor(Math.random() * Math.max(formatted.length - 3, 1)) + 2;
          setDailyDoubleIndex(ddIndex);
          return;
        }
      }
      // Fallback to mock
      setAccessLevel('demo');
      const mockQuestions = generateMockQuestions();
      setQuestions(mockQuestions);
      const ddIndex = Math.floor(Math.random() * 6) + 3;
      setDailyDoubleIndex(ddIndex);
    } catch (error) {
      console.error('Error fetching questions:', error);
      setAccessLevel('demo');
      const mockQuestions = generateMockQuestions();
      setQuestions(mockQuestions);
      const ddIndex = Math.floor(Math.random() * 6) + 3;
      setDailyDoubleIndex(ddIndex);
    }
  };

  const generateMockQuestions = () => {
    const youthQuestions = [
      {
        category: 'Creation Stories',
        question: 'God created this on the first day',
        options: ['What is light?', 'What is the sun?', 'What are plants?', 'What are animals?'],
        correct_answer: 'What is light?',
        explanation: "On the first day of creation, God said 'Let there be light,' and there was light.",
        scripture_ref: 'Genesis 1:3',
        points: 100
      },
      {
        category: 'Creation Stories',
        question: 'The number of days it took God to create the world',
        options: ['What is six?', 'What is seven?', 'What is five?', 'What is eight?'],
        correct_answer: 'What is six?',
        explanation: 'God created the world in six days and rested on the seventh day.',
        scripture_ref: 'Genesis 1:31-2:2',
        points: 200
      },
      {
        category: 'Famous Miracles',
        question: 'Jesus walked on this',
        options: ['What is water?', 'What is sand?', 'What are clouds?', 'What is fire?'],
        correct_answer: 'What is water?',
        explanation: 'Jesus walked on water during a storm, demonstrating His power over nature.',
        scripture_ref: 'Matthew 14:22-33',
        points: 100
      },
      {
        category: 'Famous Miracles',
        question: 'The number of loaves Jesus used to feed 5,000 people',
        options: ['What is five?', 'What is two?', 'What is seven?', 'What is twelve?'],
        correct_answer: 'What is five?',
        explanation: 'Jesus fed 5,000 people with five loaves and two fish.',
        scripture_ref: 'Matthew 14:17-21',
        points: 200
      },
      {
        category: 'Bible Heroes',
        question: 'The strongest man in the Bible',
        options: ['Who is Samson?', 'Who is David?', 'Who is Goliath?', 'Who is Joshua?'],
        correct_answer: 'Who is Samson?',
        explanation: 'Samson was given supernatural strength by God as long as he kept his Nazirite vow.',
        scripture_ref: 'Judges 13-16',
        points: 100
      },
      {
        category: 'Bible Heroes',
        question: 'The disciple who doubted Jesus rose from the dead',
        options: ['Who is Thomas?', 'Who is Peter?', 'Who is John?', 'Who is James?'],
        correct_answer: 'Who is Thomas?',
        explanation: 'Thomas doubted until he saw Jesus and touched His wounds.',
        scripture_ref: 'John 20:24-29',
        points: 200
      },
      {
        category: 'Famous Prayers',
        question: "This prayer starts with 'Our Father'",
        options: ["What is the Lord's Prayer?", 'What is a blessing?', 'What is a psalm?', 'What is a hymn?'],
        correct_answer: "What is the Lord's Prayer?",
        explanation: "Jesus taught His disciples the Lord's Prayer, which begins with 'Our Father in heaven.'",
        scripture_ref: 'Matthew 6:9-13',
        points: 100
      },
      {
        category: 'Famous Prayers',
        question: 'The number of times Jesus prayed in the Garden of Gethsemane',
        options: ['What is three?', 'What is one?', 'What is seven?', 'What is twelve?'],
        correct_answer: 'What is three?',
        explanation: 'Jesus prayed three times in the Garden before His arrest.',
        scripture_ref: 'Matthew 26:39-44',
        points: 200
      },
      {
        category: 'Animals in the Bible',
        question: 'This animal talked to Balaam',
        options: ['What is a donkey?', 'What is a snake?', 'What is a lion?', 'What is a dove?'],
        correct_answer: 'What is a donkey?',
        explanation: "God opened the mouth of Balaam's donkey to speak and warn him.",
        scripture_ref: 'Numbers 22:28',
        points: 100
      },
      {
        category: 'Animals in the Bible',
        question: 'The animal that appeared as a symbol of the Holy Spirit at Jesus\' baptism',
        options: ['What is a dove?', 'What is an eagle?', 'What is a lamb?', 'What is a lion?'],
        correct_answer: 'What is a dove?',
        explanation: 'The Holy Spirit descended like a dove when Jesus was baptized.',
        scripture_ref: 'Matthew 3:16',
        points: 200
      }
    ];

    const adultQuestions = [
      {
        category: 'Pauline Theology',
        question: 'Paul describes this as the "righteousness of God" revealed in the gospel',
        options: ['What is justification by faith?', 'What is sanctification?', 'What is glorification?', 'What is propitiation?'],
        correct_answer: 'What is justification by faith?',
        explanation: 'In Romans, Paul reveals that the righteousness of God is made available through faith in Jesus Christ.',
        scripture_ref: 'Romans 1:17, 3:21-22',
        points: 100
      },
      {
        category: 'Pauline Theology',
        question: 'The term Paul uses for the believer being "in Christ"',
        options: ['What is union with Christ?', 'What is imputation?', 'What is adoption?', 'What is regeneration?'],
        correct_answer: 'What is union with Christ?',
        explanation: 'Paul frequently speaks of believers being "in Christ," signifying spiritual union.',
        scripture_ref: 'Romans 6:1-11, Ephesians 1:3-14',
        points: 200
      },
      {
        category: 'Messianic Prophecies',
        question: 'Isaiah 53 prophesies about this aspect of the Messiah',
        options: ['What is His suffering and death?', 'What is His royal reign?', 'What is His birth?', 'What is His miracles?'],
        correct_answer: 'What is His suffering and death?',
        explanation: 'Isaiah 53 vividly describes the Suffering Servant who would bear our sins and be pierced for our transgressions.',
        scripture_ref: 'Isaiah 53',
        points: 100
      },
      {
        category: 'Messianic Prophecies',
        question: 'The prophet who foretold the Messiah would be born in Bethlehem',
        options: ['Who is Micah?', 'Who is Isaiah?', 'Who is Jeremiah?', 'Who is Ezekiel?'],
        correct_answer: 'Who is Micah?',
        explanation: 'Micah prophesied that the ruler of Israel would come from Bethlehem.',
        scripture_ref: 'Micah 5:2',
        points: 200
      },
      {
        category: 'Covenants',
        question: 'The theological term for the new covenant replacing the old',
        options: ['What is fulfillment?', 'What is substitution?', 'What is abolishment?', 'What is renovation?'],
        correct_answer: 'What is fulfillment?',
        explanation: 'Jesus came to fulfill the law and prophets, establishing a new covenant through His blood.',
        scripture_ref: 'Matthew 5:17, Hebrews 8:13',
        points: 100
      },
      {
        category: 'Covenants',
        question: 'The sign of the Abrahamic covenant',
        options: ['What is circumcision?', 'What is baptism?', 'What is the rainbow?', 'What is the Sabbath?'],
        correct_answer: 'What is circumcision?',
        explanation: 'God commanded Abraham and his descendants to be circumcised as a sign of the covenant.',
        scripture_ref: 'Genesis 17:9-14',
        points: 200
      },
      {
        category: 'Systematic Theology',
        question: 'The doctrine that God chose people for salvation before creation',
        options: ['What is predestination?', 'What is foreknowledge?', 'What is providence?', 'What is sovereignty?'],
        correct_answer: 'What is predestination?',
        explanation: 'Predestination refers to God\'s eternal decree to choose some for salvation according to His will.',
        scripture_ref: 'Ephesians 1:4-5, Romans 8:29-30',
        points: 100
      },
      {
        category: 'Systematic Theology',
        question: 'The theological term for Christ taking on human nature',
        options: ['What is the Incarnation?', 'What is the Hypostatic Union?', 'What is Kenosis?', 'What is Theosis?'],
        correct_answer: 'What is the Incarnation?',
        explanation: 'The Incarnation refers to the Word becoming flesh when Jesus was conceived.',
        scripture_ref: 'John 1:14, Philippians 2:6-8',
        points: 200
      },
      {
        category: 'Spiritual Gifts',
        question: 'Paul lists these gifts in 1 Corinthians 12 as manifestations of the Spirit',
        options: ['What are charismata?', 'What are fruits?', 'What are talents?', 'What are works?'],
        correct_answer: 'What are charismata?',
        explanation: 'Charismata (spiritual gifts) are given by the Holy Spirit for the common good of the church.',
        scripture_ref: '1 Corinthians 12:4-11',
        points: 100
      },
      {
        category: 'Spiritual Gifts',
        question: 'The fruit of the Spirit listed first in Galatians 5',
        options: ['What is love?', 'What is joy?', 'What is peace?', 'What is patience?'],
        correct_answer: 'What is love?',
        explanation: 'Love is the first and primary fruit of the Spirit listed by Paul.',
        scripture_ref: 'Galatians 5:22-23',
        points: 200
      }
    ];

    const questionBank = edition === 'youth' ? youthQuestions : adultQuestions;
    
    return questionBank.map((q, index) => ({
      id: index,
      categoryIndex: Math.floor(index / 2),
      pointIndex: index % 2,
      ...q
    }));
  };

  const selectQuestion = (questionId) => {
    // Check if already answered
    if (answeredQuestions.includes(questionId)) return;
    
    const question = questions.find(q => q.id === questionId);
    setSelectedQuestion(question);
    setShowBoard(false);
    
    // Check if it's a Daily Double
    if (questionId === dailyDoubleIndex) {
      setDailyDoubleRevealed(true);
    }
  };

  const handleDailyDoubleWager = (amount) => {
    setWager(amount);
    setDailyDoubleRevealed(false);
  };

  const handleAnswer = (answer) => {
    if (showResult) return;
    
    setSelectedAnswer(answer);
    setShowResult(true);
    
    const isCorrect = answer === selectedQuestion.correct_answer;
    
    if (selectedQuestion.id === dailyDoubleIndex && wager > 0) {
      // Daily Double scoring
      if (isCorrect) {
        setScore(score + wager);
      } else {
        setScore(score - wager);
      }
    } else {
      // Regular scoring
      if (isCorrect) {
        setScore(score + selectedQuestion.points);
      }
    }
  };

  const returnToBoard = () => {
    setAnsweredQuestions([...answeredQuestions, selectedQuestion.id]);
    setSelectedQuestion(null);
    setShowResult(false);
    setShowBoard(true);
    setWager(0);
    
    // Check if all questions answered
    if (answeredQuestions.length + 1 >= questions.length) {
      setGameOver(true);
    }
  };

  const restartGame = () => {
    setScore(0);
    setShowResult(false);
    setGameOver(false);
    setAnsweredQuestions([]);
    setSelectedQuestion(null);
    setShowBoard(true);
    setDailyDoubleRevealed(false);
    setDailyDoubleIndex(null);
    setWager(0);
    fetchQuestions();
  };

  if (questions.length === 0) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 flex items-center justify-center">
        <div className="text-white text-2xl">Loading questions...</div>
      </div>
    );
  }

  if (gameOver) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 p-4 sm:p-6 lg:p-8">
        <div className="container mx-auto max-w-4xl">
          <Card className="bg-gradient-to-br from-blue-50 to-purple-50 border-2 sm:border-4 border-purple-300">
            <CardContent className="p-6 sm:p-8 lg:p-12 text-center">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-800 mb-4">
                {score >= 800 ? '🏆 Champion! 🏆' : 'Game Complete!'}
              </h2>
              <div className="flex justify-center mb-4 sm:mb-6">
                <BountyDisplay amount={score} size="xl" />
              </div>
              <p className="text-xl text-slate-600 mb-2">Heaven's Bounty Earned!</p>
              <p className="text-lg sm:text-xl lg:text-2xl text-slate-700 mb-6 sm:mb-8">
                You completed all {questions.length} Jeopardy-style questions!
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <Button
                  onClick={restartGame}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg w-full sm:w-auto"
                >
                  Play Again
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  className="bg-slate-600 hover:bg-slate-700 text-white font-bold px-6 sm:px-8 py-3 sm:py-4 text-base sm:text-lg w-full sm:w-auto"
                >
                  Back to Home
                </Button>
              </div>
              
              {/* Post-Game Conversion Prompt — only for demo users */}
              {accessLevel === 'demo' && (
              <div className="mt-6 sm:mt-8 p-5 sm:p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200" data-testid="post-game-conversion">
                <p className="text-base sm:text-lg font-semibold text-slate-800 mb-2">Enjoyed this?</p>
                <p className="text-sm text-slate-600 mb-4">Unlock full lessons and interactive experiences with the complete study workbooks.</p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <Button
                    onClick={() => navigate('/quick-order')}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-bold px-6 py-3"
                    data-testid="post-game-full-access-btn"
                  >
                    Get Full Access
                  </Button>
                  <Button
                    onClick={() => { navigate('/'); setTimeout(() => document.getElementById('bundle-offer')?.scrollIntoView({ behavior: 'smooth' }), 500); }}
                    variant="outline"
                    className="border-2 border-amber-500 text-amber-700 hover:bg-amber-50 font-bold px-6 py-3"
                    data-testid="post-game-view-bundle-btn"
                  >
                    View Bundle
                  </Button>
                </div>
              </div>
              )}
              
              <GameDisclaimer />
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Daily Double modal
  if (dailyDoubleRevealed && !showResult && wager === 0) {
    const allInWager = Math.max(score, 100);
    const halfWager = Math.max(Math.floor(score / 2), 50);
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 p-4 sm:p-6 lg:p-8 flex items-center justify-center">
        <Card className="bg-gradient-to-br from-yellow-50 to-amber-50 border-2 sm:border-4 border-yellow-400 max-w-2xl w-full mx-4">
          <CardContent className="p-6 sm:p-8 lg:p-12 text-center">
            <h2 className="text-3xl sm:text-4xl lg:text-6xl font-bold text-yellow-600 mb-4 sm:mb-6 animate-pulse">
              ✨ DAILY DOUBLE! ✨
            </h2>
            <p className="text-lg sm:text-xl lg:text-2xl text-slate-800 mb-3 sm:mb-4">
              You found the hidden Daily Double!
            </p>
            <div className="flex justify-center mb-4">
              <BountyDisplay amount={score} size="lg" />
            </div>
            <p className="text-sm sm:text-base lg:text-lg text-slate-600 mb-4 sm:mb-6">
              Pick your wager — single player only.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4" data-testid="daily-double-wager-options">
              <Button
                onClick={() => handleDailyDoubleWager(allInWager)}
                className="bg-red-600 hover:bg-red-700 text-white font-bold py-3 sm:py-4 text-base sm:text-lg"
                data-testid="wager-all-btn"
              >
                All In ({allInWager.toLocaleString()})
              </Button>
              <Button
                onClick={() => handleDailyDoubleWager(halfWager)}
                className="bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 sm:py-4 text-base sm:text-lg"
                data-testid="wager-half-btn"
              >
                Half ({halfWager.toLocaleString()})
              </Button>
              <Button
                onClick={() => handleDailyDoubleWager(1)}
                className="bg-yellow-500 hover:bg-yellow-600 text-white font-bold py-3 sm:py-4 text-base sm:text-lg"
                data-testid="wager-one-btn"
              >
                $1 (Safe)
              </Button>
            </div>
            <GameDisclaimer />
          </CardContent>
        </Card>
      </div>
    );
  }

  // Jeopardy Board View
  if (showBoard) {

  return (
      <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 p-4 sm:p-6 lg:p-8">
        <div className="container mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-4 sm:mb-6">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mb-4">
              <button
                onClick={() => navigate('/')}
                className="bg-slate-700 hover:bg-slate-600 text-white font-semibold px-4 py-2 rounded-lg transition-all flex items-center gap-2 text-sm sm:text-base"
              >
                ← Back to Home
              </button>
              <div className="flex items-center gap-2">
                <Badge className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 sm:px-6 py-2 text-base sm:text-lg">
                  {edition === 'youth' ? 'Youth Edition' : 'Adult Edition'}
                </Badge>
                <Badge data-testid="access-level-badge" className={`px-3 py-2 text-sm font-semibold ${
                  accessLevel === 'full'
                    ? 'bg-green-500/30 text-green-300 border border-green-500/50'
                    : 'bg-amber-500/30 text-amber-300 border border-amber-500/50'
                }`}>
                  {accessLevel === 'full' ? 'Full Board' : 'Demo Board'}
                </Badge>
              </div>
            </div>
            <div className="text-center">
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-white mb-2">Tricky Testaments</h1>
              <p className="text-sm sm:text-base text-purple-300">Pick a category and earn Stacks!</p>
              {accessLevel === 'demo' && (
                <p className="text-xs text-amber-300/70 mt-1">
                  Demo mode — 2 rows shown. Purchase to unlock the full 5-row board.
                </p>
              )}
            </div>
          </div>

          {/* Score Display */}
          <div className="text-center mb-4 sm:mb-6">
            <div className="inline-flex flex-col items-center bg-gradient-to-r from-blue-600 to-purple-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-xl">
              <div className="text-xs sm:text-sm text-blue-100">Heaven's Bounty</div>
              <BountyDisplay amount={score} size="lg" />
              <div className="text-xs sm:text-sm text-blue-100 mt-1">{answeredQuestions.length}/{questions.length} Answered</div>
            </div>
          </div>

          {/* Jeopardy Board — true grid layout */}
          <div className="bg-blue-950 p-3 sm:p-4 lg:p-6 rounded-xl shadow-2xl border-2 sm:border-4 border-yellow-400" data-testid="jeopardy-board">
            {/* Grid: categories as columns, point values as rows */}
            <div className="grid grid-cols-5 gap-2 sm:gap-3">
              {/* Category Headers (row 1) */}
              {categories.map((category, catIndex) => (
                <div key={`hdr-${catIndex}`} className="bg-blue-800 text-white p-2 sm:p-3 rounded-lg text-center font-bold text-xs sm:text-sm lg:text-base border-2 border-blue-600 min-h-[60px] sm:min-h-[70px] flex items-center justify-center" data-testid={`category-header-${catIndex}`}>
                  {category}
                </div>
              ))}

              {/* Question tiles — row by row */}
              {pointValues.map((points, rowIndex) => (
                categories.map((_, catIndex) => {
                  const question = questions.find(
                    q => q.categoryIndex === catIndex && q.pointIndex === rowIndex
                  );
                  const isAnswered = answeredQuestions.includes(question?.id);

                  return (
                    <button
                      key={`q-${catIndex}-${rowIndex}`}
                      onClick={() => !isAnswered && question && selectQuestion(question.id)}
                      disabled={isAnswered || !question}
                      className={`
                        p-3 sm:p-4 lg:p-6 rounded-lg text-xl sm:text-2xl lg:text-3xl font-bold transition-all
                        ${isAnswered 
                          ? 'bg-blue-950 text-blue-900 cursor-not-allowed border-2 border-blue-900' 
                          : question
                            ? 'bg-blue-600 text-yellow-400 hover:bg-blue-500 hover:scale-105 cursor-pointer border-2 border-yellow-500 shadow-lg'
                            : 'bg-blue-900 text-blue-800 border-2 border-blue-800 cursor-not-allowed'
                        }
                      `}
                      data-testid={`tile-${catIndex}-${rowIndex}`}
                    >
                      {isAnswered ? '✓' : points}
                    </button>
                  );
                })
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Question View — Recall-based (no MCQ)
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 p-4 sm:p-6 lg:p-8">
      <div className="container mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row justify-between items-center gap-3 mb-4">
            <Badge className="bg-gradient-to-r from-blue-500 to-purple-500 text-white px-4 sm:px-6 py-2 text-base sm:text-lg">
              {edition === 'youth' ? 'Youth Edition' : 'Adult Edition'}
            </Badge>
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg">
              <BountyDisplay amount={score} size="md" />
            </div>
          </div>
          <div className="text-center">
            <h1 className="text-2xl sm:text-3xl font-bold text-white mb-1">Tricky Testaments</h1>
            <p className="text-sm text-purple-300">Think... then reveal!</p>
          </div>
        </div>

        {/* Question Card */}
        <Card className="bg-white mb-4 sm:mb-6">
          <CardHeader className="bg-gradient-to-r from-blue-100 to-purple-100">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
              <CardTitle className="text-lg sm:text-xl">
                {selectedQuestion.category}
                {selectedQuestion.id === dailyDoubleIndex && wager > 0 && (
                  <Badge className="ml-3 bg-yellow-500 text-white text-xs">
                    DAILY DOUBLE - Wager: {wager}
                  </Badge>
                )}
              </CardTitle>
              <Badge className="bg-purple-600 text-white text-sm sm:text-base whitespace-nowrap">
                {selectedQuestion.id === dailyDoubleIndex && wager > 0
                  ? `Wager: ${wager}`
                  : `${selectedQuestion.points} Points`}
              </Badge>
            </div>
          </CardHeader>
          <CardContent className="p-4 sm:p-6 lg:p-8">
            {/* Question Prompt */}
            <div className="bg-blue-900 text-white p-5 sm:p-8 rounded-xl mb-6 text-center" data-testid="question-prompt">
              <p className="text-lg sm:text-xl lg:text-2xl font-semibold leading-relaxed">
                {selectedQuestion.question}
              </p>
            </div>

            {!showResult ? (
              /* Reveal Answer Button */
              <div className="text-center">
                <Button
                  onClick={() => setShowResult(true)}
                  className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold px-10 py-4 text-lg rounded-xl shadow-lg transform hover:scale-105 transition-all"
                  data-testid="reveal-answer-btn"
                >
                  Reveal Answer
                </Button>
              </div>
            ) : (
              /* Answer + Self-Score */
              <div>
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border-2 border-indigo-300 rounded-xl p-5 sm:p-6 mb-5" data-testid="revealed-answer">
                  <p className="text-xs uppercase tracking-wider text-indigo-500 font-semibold mb-2">Answer</p>
                  <p className="text-lg sm:text-xl font-bold text-slate-800 mb-3">{selectedQuestion.correct_answer}</p>
                  {selectedQuestion.explanation && (
                    <p className="text-sm text-slate-600 mb-2">{selectedQuestion.explanation}</p>
                  )}
                  {selectedQuestion.scripture_ref && (
                    <p className="text-xs text-slate-500">Scripture: {selectedQuestion.scripture_ref}</p>
                  )}
                </div>

                <p className="text-center text-slate-600 text-sm mb-3">Did you know the answer?</p>
                <div className="flex gap-4 justify-center" data-testid="self-score-buttons">
                  <Button
                    onClick={() => {
                      const pts = selectedQuestion.id === dailyDoubleIndex && wager > 0 ? wager : selectedQuestion.points;
                      setScore(score + pts);
                      returnToBoard();
                    }}
                    className="bg-green-600 hover:bg-green-700 text-white font-bold px-8 py-4 text-base rounded-xl flex-1 max-w-[200px]"
                    data-testid="got-it-btn"
                  >
                    Got it! +{selectedQuestion.id === dailyDoubleIndex && wager > 0 ? wager : selectedQuestion.points}
                  </Button>
                  <Button
                    onClick={() => {
                      // Deduct points on wrong answer (Daily Double uses wager, regular uses point value)
                      const deduction = (selectedQuestion.id === dailyDoubleIndex && wager > 0) ? wager : selectedQuestion.points;
                      setScore(score - deduction);
                      returnToBoard();
                    }}
                    className="bg-red-500 hover:bg-red-600 text-white font-bold px-8 py-4 text-base rounded-xl flex-1 max-w-[200px]"
                    data-testid="missed-it-btn"
                  >
                    Missed it -{selectedQuestion.id === dailyDoubleIndex && wager > 0 ? wager : selectedQuestion.points}
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <GameDisclaimer />
      </div>
    </div>
  );
};

export default TrickyTestamentGame;
